import React, { useState } from 'react';
import { structureAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Building2, Users, Edit2, Briefcase, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

const DepartmentsTab = ({ departments, roles, loading, onUpdate }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const openCreateDialog = () => {
    setSelectedDept(null);
    setFormData({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEditDialog = (dept) => {
    setSelectedDept(dept);
    setFormData({ name: dept.name, description: dept.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Department name is required');
      return;
    }

    try {
      setSaving(true);
      
      if (selectedDept) {
        await structureAPI.updateDepartment(selectedDept.id, formData);
        toast.success('Department updated successfully');
      } else {
        await structureAPI.createDepartment(formData);
        toast.success('Department created successfully');
      }
      
      setDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error saving department:', error);
      toast.error('Failed to save department');
    } finally {
      setSaving(false);
    }
  };

  // Calculate stats for each department
  const getDeptStats = (deptId) => {
    const deptRoles = roles.filter(r => r.departmentId === deptId && r.status !== 'archived');
    const filledRoles = deptRoles.filter(r => r.assignedPersonId);
    const vacantRoles = deptRoles.filter(r => !r.assignedPersonId);
    
    return {
      totalRoles: deptRoles.length,
      filledRoles: filledRoles.length,
      vacantRoles: vacantRoles.length
    };
  };

  if (loading) {
    return (
      <div 
        className="flex items-center justify-center h-64 rounded-xl"
        style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
      >
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" 
          style={{ borderColor: '#2A9D8F', borderTopColor: 'transparent' }} 
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="departments-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>Departments</h2>
          <p className="text-sm" style={{ color: '#8D99AE' }}>
            Manage your organization's departments
          </p>
        </div>
        <Button
          onClick={openCreateDialog}
          style={{ backgroundColor: '#2A9D8F', color: '#FFFFFF' }}
          data-testid="add-department-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Department
        </Button>
      </div>

      {/* Department Cards Grid */}
      {departments.length === 0 ? (
        <div 
          className="flex flex-col items-center justify-center py-16 rounded-xl"
          style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}
          data-testid="departments-empty"
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: '#E8F4F2' }}
          >
            <Building2 className="w-8 h-8" style={{ color: '#2A9D8F' }} />
          </div>
          <h3 className="text-lg font-medium mb-2" style={{ color: '#264653' }}>
            No departments yet
          </h3>
          <p className="text-sm mb-4 text-center max-w-md" style={{ color: '#8D99AE' }}>
            Create departments to organize your roles and team members
          </p>
          <Button
            onClick={openCreateDialog}
            style={{ backgroundColor: '#2A9D8F', color: '#FFFFFF' }}
          >
            Create First Department
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {departments.map(dept => {
            const stats = getDeptStats(dept.id);
            
            return (
              <Card 
                key={dept.id} 
                className="hover:shadow-md transition-shadow"
                data-testid={`department-card-${dept.id}`}
              >
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: '#E8F4F2' }}
                    >
                      <Building2 className="w-5 h-5" style={{ color: '#2A9D8F' }} />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold" style={{ color: '#264653' }}>
                        {dept.name}
                      </CardTitle>
                      {dept.description && (
                        <p className="text-xs mt-1 line-clamp-2" style={{ color: '#8D99AE' }}>
                          {dept.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4" style={{ color: '#6B7280' }} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(dept)}>
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit Department
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent className="pt-2">
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-2">
                    <div 
                      className="p-3 rounded-lg text-center"
                      style={{ backgroundColor: '#F9FAFB' }}
                    >
                      <div className="flex items-center justify-center mb-1">
                        <Briefcase className="w-4 h-4" style={{ color: '#6B7280' }} />
                      </div>
                      <p className="text-lg font-semibold" style={{ color: '#264653' }}>
                        {stats.totalRoles}
                      </p>
                      <p className="text-xs" style={{ color: '#8D99AE' }}>Roles</p>
                    </div>
                    <div 
                      className="p-3 rounded-lg text-center"
                      style={{ backgroundColor: '#D1FAE5' }}
                    >
                      <div className="flex items-center justify-center mb-1">
                        <Users className="w-4 h-4" style={{ color: '#065F46' }} />
                      </div>
                      <p className="text-lg font-semibold" style={{ color: '#065F46' }}>
                        {stats.filledRoles}
                      </p>
                      <p className="text-xs" style={{ color: '#065F46' }}>Filled</p>
                    </div>
                    <div 
                      className="p-3 rounded-lg text-center"
                      style={{ backgroundColor: '#FEF3C7' }}
                    >
                      <div className="flex items-center justify-center mb-1">
                        <Users className="w-4 h-4" style={{ color: '#92400E' }} />
                      </div>
                      <p className="text-lg font-semibold" style={{ color: '#92400E' }}>
                        {stats.vacantRoles}
                      </p>
                      <p className="text-xs" style={{ color: '#92400E' }}>Vacant</p>
                    </div>
                  </div>
                  
                  {/* Status badge */}
                  <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: '#E5E7EB' }}>
                    <Badge 
                      variant="outline"
                      style={{ 
                        backgroundColor: dept.status === 'active' ? '#D1FAE5' : '#F3F4F6',
                        color: dept.status === 'active' ? '#065F46' : '#6B7280',
                        borderColor: 'transparent'
                      }}
                    >
                      {dept.status === 'active' ? 'Active' : 'Archived'}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(dept)}
                      style={{ color: '#2A9D8F' }}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="department-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedDept ? 'Edit Department' : 'Create Department'}
            </DialogTitle>
            <DialogDescription>
              {selectedDept 
                ? 'Update the department details'
                : 'Add a new department to your organization'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="dept-name">Department Name *</Label>
              <Input
                id="dept-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Engineering"
                data-testid="department-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="dept-desc">Description</Label>
              <Textarea
                id="dept-desc"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the department..."
                rows={3}
                data-testid="department-desc-input"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: '#2A9D8F', color: '#FFFFFF' }}
              data-testid="save-department-btn"
            >
              {saving ? 'Saving...' : selectedDept ? 'Save Changes' : 'Create Department'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DepartmentsTab;
